/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { PRODUCT_DISCOVERY_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import type { Project, ProjectData, ProjectsResponse } from './ProjectsResponse.types.tsx';

const createGetJpdProjectsUrl = (startAt: number, maxResults: number) => {
	const queryParams = new URLSearchParams({
		typeKey: PRODUCT_DISCOVERY_PROJECT,
		expand: 'issueTypes',
		maxResults: maxResults.toString(),
		startAt: startAt.toString(),
	});

	return `/rest/api/3/project/search?${queryParams.toString()}`;
};

const mapRawProjectToProject = (rawProject: ProjectsResponse['values']['0']): Project => ({
	id: rawProject.id,
	key: rawProject.key,
	name: rawProject.name,
	projectTypeKey: rawProject.projectTypeKey,
	avatarUrl: rawProject.avatarUrls['16x16'],
	ideaIssueTypeId: rawProject.issueTypes[0]?.id || '',
});

export const getJpdProjects = async (): Promise<Project[]> => {
	const maxPageSize = 50; // a value more than 50 fallbacks to 50

	const firstProjectsPage = await fetchJson<ProjectsResponse>(
		createGetJpdProjectsUrl(0, maxPageSize),
	);

	const projects = firstProjectsPage.values;

	let isLastPage = firstProjectsPage.isLast;
	let nextPageUrl = firstProjectsPage.nextPage;

	while (!isLastPage) {
		// eslint-disable-next-line no-await-in-loop
		const nextProjectsPage = await fetchJson<ProjectsResponse>(nextPageUrl);
		isLastPage = nextProjectsPage.isLast;
		nextPageUrl = nextProjectsPage.nextPage;

		projects.push(...nextProjectsPage.values);
	}

	return projects.map(mapRawProjectToProject);
};

export const hasJpdProjects = async (): Promise<boolean> => {
	try {
		const { total } = await fetchJson<ProjectsResponse>(createGetJpdProjectsUrl(0, 0));

		return total > 0;
	} catch (error) {
		return false;
	}
};

/**
 * Lightweight check that avoids paging through all spaces.
 * Fetches at most `n` projects and checks whether at least `n` exist.
 */
export const hasAtLeastNJpdProjects = async (n: number): Promise<boolean> => {
	if (n <= 0) {
		return true;
	}

	try {
		const { values } = await fetchJson<ProjectsResponse>(createGetJpdProjectsUrl(0, n));
		return Array.isArray(values) && values.length >= n;
	} catch {
		return false;
	}
};

export const getRecentJpdProjects = async (): Promise<ProjectData[]> => {
	const recentProjects = await fetchJson<ProjectData[]>('/rest/api/3/project/recent');

	return recentProjects.filter((project) => project.projectTypeKey === PRODUCT_DISCOVERY_PROJECT);
};
